import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useImmer } from "use-immer";
import { RealmContext } from "./providers/RealmContext";
import {
  Box,
  Button,
  Form,
  Heading,
  PageContent,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  TextInput,
} from "grommet";
import { FormElement } from "./FormElement";
import { BSON } from "realm-web";

type Result = {
  _id: BSON.ObjectId;
  ts: Date;
  url: string;
  cases: Case[];
  comments?: Comment[];
};

type Case = {
  ts: Date;
  status: string;
  synopsis?: string;
};

type Comment = {
  ts: Date;
  text: string;
};

export function CheckStatus() {
  const { realmUser: user } = useContext(RealmContext);
  const [status, setStatus] = useImmer({ email: "", ticketId: "" });
  const [results, setResults] = useState<null | Result[]>(null);
  const [error, setError] = useState('');

  // clear cases when it closes
  useEffect(() => {
    setError('');
    setResults(null);
    setStatus((draft) => {
      draft.ticketId = "";
    });
  }, []);

  const checkTicketStatus = useCallback(
    (status: { email: string; ticketId: string }) => {
      user?.callFunction("abuseForm_getTicketStatus", { email: status.email, ticketId: status.ticketId }).then((r: Result[]) => {
        // console.log(r);
        r.sort((a: Result, b: Result) => b._id.getTimestamp().getTime() - a._id.getTimestamp().getTime());
        r.unshift(r.splice(r.findIndex((x: Result) => x._id.toString() === status.ticketId), 1)[0]);
        setResults(r);
        setError('');
      }).catch(() => {
        setError(`The email address ${status.email} did not submit report ${status.ticketId}.`);
      })
    },
    [user]
  );

  return (
    <PageContent gap="1rem" style={{ width: "900px" }}>
      <Heading level="3" margin="none">
        Check Report Status
      </Heading>
      <Form onSubmit={() => checkTicketStatus(status)}>
        <Box direction="row" gap="large" justify="center">
          <Box>
            <FormElement
              component={TextInput}
              // required={false}
              label="Your Email Address"
              help="The email address you used to submit your abuse report."
              name="email"
              value={status.email}
              placeholder="e.g. me@email.com"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setStatus((draft) => {
                  draft.email = event.target.value;
                })
              }
            />
          </Box>
          <Box>
            <FormElement
              component={TextInput}
              // required={false}
              label="Report ID"
              help="The 24-character ID you received when you submitted your abuse report."
              name="reportId"
              value={status.ticketId}
              placeholder="e.g. 34c0fbdeed37290eda61c8bd"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setStatus((draft) => {
                  draft.ticketId = event.target.value;
                })
              }
            />
          </Box>
        </Box>
        <Box direction="row" justify="center">
          <Button
            type="submit"
            disabled={
              !status.email.match(/.@(?:[\w-]+\.)+[\w-]+$/) ||
              !status.ticketId.match(/^[a-f0-9]{24}$/)
            }
            primary
            label="Check Status"
          // onClick={() => checkTicketStatus(status)}
          />
        </Box>
      </Form>
      {error !== '' && <Text color="status-error">{error}</Text>}
      {error === '' && <>
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell scope="col" style={{color: 'white'}}>
                Report ID
              </TableCell>
              <TableCell scope="col" style={{color: 'white'}}>
                Reported At
              </TableCell>
              <TableCell scope="col" style={{color: 'white'}} width="10rem">
                URL
              </TableCell>
              <TableCell scope="col" style={{color: 'white'}}>
                Status
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results?.map(({ _id, url, ts, cases, comments }, i) => {
              const color = i === 0 ? 'selected-background' : ((i % 2) ? 'background-back' : 'background-front');
              return (
                <TableRow key={_id.toString()}>
                  <TableCell background={{ color }} verticalAlign="top"><Text size="small">{_id.toString()}</Text></TableCell>
                  <TableCell background={{ color }} verticalAlign="top"><Text size="small">{ts.toLocaleString()}</Text></TableCell>
                  <TableCell background={{ color }} verticalAlign="top"><Text size="small" wordBreak="break-all">{url}</Text></TableCell>
                  <TableCell background={{ color }} gap="small">
                    <Cases timestamp={ts} cases={cases.filter((c) => c.status !== 'Suggested')} />
                    <Comments comments={comments}/>
                </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </>}
    </PageContent>
  );
}

function Cases({ timestamp, cases }: { timestamp: Date; cases: Case[] }) {
  if (cases.length === 0) {
    if (timestamp.valueOf() < Date.now() - 1000 * 60 * 60 * 96) {
      return <Text size="small"><em>Insufficient Evidence for Action</em> at {new Date (timestamp.valueOf() + 1000 * 60 * 60 * 96).toLocaleString()}</Text>;
    }
    return <Text size="small"><em>Pending Review</em></Text>;
  }

  return cases.map((c) => (
    <Box key={c.ts.toISOString()}>
      <Text size="small"><strong>{c.status === 'Canceled' ? 'Insufficient Evidence for Action' : c.status === 'With Target' ? 'Escalated' : c.status}</strong> at {c.ts.toLocaleString()}</Text>
      {c.synopsis && <Text size="small">{c.synopsis}</Text>}
    </Box>
  ));
}

function Comments({ comments }: { comments: Comment[] | undefined | null }) {
  return comments?.map((c) => (
    <Box key={c.ts.toISOString()} margin={{ top: "small" }}>
      <Text size="small">{c.ts.toLocaleString()}</Text>
      <Text size="small">{c.text}</Text>
    </Box>
  ));
}